/**
 * Frontend structured logging — mirrors backend JSON format.
 *
 * All frontend logs are emitted in JSON format with consistent structure:
 *   {
 *     "time": "2026-05-27T20:55:06.497Z",
 *     "level": 20-50 (debug-error),
 *     "pid": browser window id,
 *     "hostname": "browser",
 *     "name": "opus.module-name",
 *     "msg": "human-readable message",
 *     ...custom fields...
 *   }
 *
 * Logger hierarchy follows backend convention:
 *   - opus.app — top-level application events
 *   - opus.api — API client request/response tracking
 *   - opus.auth — authentication flows
 *   - opus.store — Zustand store mutations
 *   - opus.component — React component lifecycle (if needed)
 *
 * Critical logs (errors, auth failures) are automatically sent to backend
 * via POST /api/logs/frontend/ for centralized tracking and alerts.
 *
 * Dev mode (import.meta.env.DEV) outputs logs to console via pino/browser.
 * Prod mode (import.meta.env.PROD) outputs to console + batches errors to backend.
 */

import pino from "pino/browser";

const criticalLogBuffer: Record<string, unknown>[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function flushCriticalLogs() {
	if (criticalLogBuffer.length === 0) return;
	const logs = criticalLogBuffer.splice(0, criticalLogBuffer.length);
	fetch("/api/logs/frontend/", {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ logs }),
	}).catch(() => {});
}

function scheduleCriticalLogFlush() {
	if (flushTimer === null) {
		flushTimer = setTimeout(() => {
			flushCriticalLogs();
			flushTimer = null;
		}, 5000); // 5 second batch window
	}
}

// pino level 50 == "error" (see level table in the module doc comment above).
const CRITICAL_LEVEL_THRESHOLD = 50;

function criticalLogTransport() {
	return {
		write(msg: string) {
			try {
				const parsed = JSON.parse(msg) as { level?: number };
				if (typeof parsed.level === "number" && parsed.level >= CRITICAL_LEVEL_THRESHOLD) {
					criticalLogBuffer.push(parsed);
					scheduleCriticalLogFlush();
				}
			} catch {
				// malformed log line — drop it rather than crash logging itself
			}
		},
	};
}

function createBaseLogger() {
	// Vitest spawns worker threads for pino transports via a Node require.resolve
	// path that Vitest's module resolver can't satisfy. Skip the transport entirely
	// in test runs — silent keeps CI output clean; assertions cover behaviour.
	if (import.meta.env.VITEST) {
		return pino({ level: "silent" });
	}

	if (import.meta.env.DEV) {
		return pino({ level: "debug" });
	} else {
		// Production: pino.destination() and pino.multistream() are Node-only and
		// crash in the browser bundle. Use a single plain-object stream instead.
		const critical = criticalLogTransport();
		return pino(
			{
				level: "info",
				base: { hostname: "browser" },
				formatters: {
					level: (label) => ({ level: label }),
				},
			},
			{
				write: (msg: string) => {
					console.log(msg.trimEnd());
					critical.write(msg);
				},
			}
		);
	}
}

const rootLogger = createBaseLogger();

/**
 * Get a named logger for a module. All logs include the module name
 * in the "name" field for easy filtering.
 *
 * Examples:
 *   const logger = getLogger("opus.api");
 *   logger.info("request_started", { path: "/sites/", method: "GET" });
 *   logger.error("request_failed", { path: "/sites/", status: 500 });
 */
export function getLogger(name: string) {
	return rootLogger.child({ name });
}
